import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { useTheme } from "@mui/material/styles";
import isEqual from "lodash/isEqual";
import isNil from "lodash/isNil";
import merge from "lodash/merge";
import PropTypes from "prop-types";
import { useMemo } from "react";
const ToggleNavigation = ({
  value,
  tabs,
  onChange,
  sx,
  className,
  variant,
  useIndex = false,
  orientation = "horizontal",
  exclusive = true,
  underlinePosition = "bottom",
  allowDeselect = false,
}) => {
  const isObjectTabs = useMemo(
    () =>
      Array.isArray(tabs) &&
      tabs.length > 0 &&
      typeof tabs[0] === "object" &&
      "label" in tabs[0],
    [tabs]
  );

  const getTabLabel = (tab) => (isObjectTabs ? tab.label : tab);
  const getTabValue = (tab) => (isObjectTabs ? tab.value : tab);

  const handleOnTabSwitch = (event, newIndex) => {
    if (isNil(newIndex)) return;
    if (useIndex) {
      onChange(newIndex);
      return;
    }
    onChange(getTabValue(tabs[newIndex]));
  };

  const handleButtonClick = (event, clickedIndex) => {
    if (!allowDeselect) return;

    event.stopPropagation();

    const isCurrentlySelected = _value === clickedIndex;

    if (isCurrentlySelected) {
      onChange(null);
    } else {
      if (useIndex) {
        onChange(clickedIndex);
      } else {
        onChange(getTabValue(tabs[clickedIndex]));
      }
    }
  };

  const _value = useMemo(() => {
    if (useIndex) return value;
    const idx = tabs.findIndex((tab) => isEqual(getTabValue(tab), value));
    return idx >= 0 ? idx : null;
  }, [useIndex, tabs, value, isObjectTabs]);

  return (
    <TabSwitcherGroup
      className={className}
      orientation={orientation}
      variant={variant}
      underlinePosition={underlinePosition}
      value={_value}
      exclusive={exclusive}
      sx={sx}
      onChange={allowDeselect ? () => {} : handleOnTabSwitch}
      aria-label="toggle navigation"
    >
      {tabs.map((tab, ind) => (
        <ToggleButton
          key={ind}
          value={ind}
          onClick={allowDeselect ? (e) => handleButtonClick(e, ind) : undefined}
        >
          {getTabLabel(tab)}
        </ToggleButton>
      ))}
    </TabSwitcherGroup>
  );
};

ToggleNavigation.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object,
    PropTypes.oneOf([null]),
  ]),
  tabs: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
          .isRequired,
        value: PropTypes.any.isRequired,
      }),
    ])
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  sx: PropTypes.object,
  variant: PropTypes.oneOf(["background", "underline"]),
  useIndex: PropTypes.bool,
  orientation: PropTypes.oneOf(["horizontal", "vertical"]),
  exclusive: PropTypes.bool,
  underlinePosition: PropTypes.oneOf(["bottom", "top", "left", "right"]),
  allowDeselect: PropTypes.bool,
};

const TabSwitcherGroup = ({
  value,
  children,
  full,
  exclusive,
  className,
  onChange,
  orientation = "horizontal",
  ariaLabel,
  variant = "background",
  underlinePosition = "bottom",
  sx,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => {
    const baseStyles = {
      flexGrow: full ? 1 : 0,
      ".MuiButtonBase-root": {
        flexGrow: full ? 1 : 0,
        color: "#181d1f",
        fontWeight: 400,
        fontSize: "13px",
        ...(orientation === "vertical" && {
          whiteSpace: "nowrap",
          minWidth: "fit-content",
          justifyContent: "flex-start",
          borderLeft: "2px solid transparent",
        }),
        "&.Mui-selected": {
          color: "#2962ff",
          fontWeight: 700,
          ...(variant === "underline"
            ? {
                background: "none",
                borderRadius: "0 !important",
                fontWeight: 500,
              }
            : { background: "#2962ff14" }),
          ...(variant === "underline" && {
            borderTop: "none",
            borderRight: "none",
            borderBottom: "none",
            borderLeft: "none",
            ...(orientation === "horizontal" &&
              underlinePosition === "bottom" && {
                borderBottom: "2px solid #2962ff",
              }),
            ...(orientation === "horizontal" &&
              underlinePosition === "top" && {
                borderTop: "2px solid #2962ff",
              }),
            ...(orientation === "vertical" &&
              underlinePosition === "left" && {
                borderLeft: "2px solid #2962ff",
              }),
            ...(orientation === "vertical" &&
              underlinePosition === "right" && {
                borderRight: "2px solid #2962ff",
              }),
          }),
        },
      },
      "& .MuiToggleButtonGroup-grouped": {
        margin: theme.spacing(0.2),
        border: 0,
        borderLeft: "2px solid transparent",
        textTransform: "capitalize",
        "&.Mui-disabled": { border: 0 },
        "&:not(:first-of-type)": { borderRadius: "4px" },
        "&:first-of-type": { borderRadius: "4px" },
      },
    };

    const variants = {
      underline: {
        ".MuiButtonBase-root": { fontSize: "12px" },
      },
    };

    return merge({}, baseStyles, variants[variant] ?? {}, sx);
  }, [sx, variant, full, theme, orientation, underlinePosition]);

  return (
    <ToggleButtonGroup
      size="small"
      className={`toggle-navigation-root ${className}`}
      orientation={orientation}
      value={value}
      exclusive={exclusive}
      onChange={onChange}
      aria-label={ariaLabel}
      sx={styles}
    >
      {children}
    </ToggleButtonGroup>
  );
};

TabSwitcherGroup.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.oneOf([null]),
  ]),
  children: PropTypes.node.isRequired,
  full: PropTypes.bool,
  exclusive: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  orientation: PropTypes.oneOf(["horizontal", "vertical"]),
  ariaLabel: PropTypes.string,
  variant: PropTypes.oneOf(["background", "underline"]),
  underlinePosition: PropTypes.oneOf(["bottom", "top", "left", "right"]),
  sx: PropTypes.object,
};

export default ToggleNavigation;
